import { useAuth as useAuthContext } from '@/contexts/AuthContext';
import { useAuth as useAuthStore } from '@/store/auth';
import { ArrowLeft } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { authService } from '../../services/authService/authService';

export default function OtpVerification() {
  const nav = useNavigate();
  const setOtp = useAuthStore((s) => s.setOtp);
  const authenticate = useAuthStore((s) => s.authenticate);
  const email = useAuthStore((s) => s.email);
  const phone = useAuthStore((s) => s.phone);
  const setPhone = useAuthStore((s) => s.setPhone);
  const setEmail = useAuthStore((s) => s.setEmail);
  const { signIn } = useAuthContext();
  const location = useLocation();
  const [digits, setDigits] = useState(Array(6).fill(''));
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);

  const [error, setError] = useState('');
  const [canResend, setCanResend] = useState(false);
  const inputs = useRef<Array<HTMLInputElement | null>>([]);

  const getInitialTimer = () => {
    const expiry = sessionStorage.getItem('otpTimerExpiry');
    if (!expiry) return 120;
    const remaining = Math.floor((Number(expiry) - Date.now()) / 1000);
    return remaining > 0 ? remaining : 0;
  };

  const initialTimer = getInitialTimer();
  const [timer, setTimer] = useState(initialTimer);
  // If timer was already expired when the component mounts (e.g. after a long reload), allow resend right away
  useEffect(() => {
    if (initialTimer === 0) setCanResend(true);
  }, []);

  const phoneFromState = (location.state as any)?.phone;
  const usernameFromState = (location.state as any)?.username;

  useEffect(() => {
    inputs.current[0]?.focus();
  }, []);

  // useEffect(() => {
  //   if (timer > 0) {
  //     const interval = setInterval(() => {
  //       setTimer((prev) => prev - 1);
  //     }, 1000);
  //     return () => clearInterval(interval);
  //   } else {
  //     setCanResend(true);
  //   }
  // }, [timer]);
  useEffect(() => {
    let interval: NodeJS.Timeout | undefined;

    if (timer > 0) {
      interval = setInterval(() => {
        setTimer((prev) => prev - 1);
      }, 1000);
    } else {
      setCanResend(true); // enable resend when timer ends
    }

    return () => {
      if (interval) clearInterval(interval);
    };
  }, [timer]);

  function handleChange(idx: number, val: string) {
    if (!/^\d?$/.test(val)) return;
    const next = [...digits];
    next[idx] = val;
    setDigits(next);
    if (val && idx < 5) inputs.current[idx + 1]?.focus();
  }

  function handleKeyDown(idx: number, e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Backspace' && !digits[idx] && idx > 0) {
      inputs.current[idx - 1]?.focus();
    }
  }

  const handleResend = async () => {
    if (!canResend) return;

    setResendLoading(true);
    setError('');

    try {
      await authService.resendOTP(phoneFromState || phone);
      setDigits(Array(6).fill(''));
      setOtp('');
      setTimeout(() => {
        inputs.current[0]?.focus();
      }, 0);
      setError('');
      sessionStorage.setItem('otpTimerExpiry', String(Date.now() + 120_000));
      setTimer(120);
      setCanResend(false);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setResendLoading(false);
    }
  };

  async function handleConfirm() {
    const code = digits.join('');
    if (code.length !== 6) {
      setError('Please enter 6-digit OTP');
      return;
    }

    setConfirmLoading(true);
    setError('');

    try {
      const response = await authService.verifyOTP(
        phoneFromState || phone,
        code,
        usernameFromState,
      );

      sessionStorage.removeItem('otpTimerExpiry');
      setOtp(code);
      authenticate();

      // Sign in via AuthContext
      signIn(email || phone || phoneFromState || '');

      // Reset auth store
      setPhone('');
      setEmail('');

      // Navigate to dashboard
      nav('/dashboard');
    } catch (err: any) {
      const errorMessage = err.message || 'Failed to verify OTP';
      setError(errorMessage);

      if (
        errorMessage.includes('session has expired') ||
        errorMessage.includes('request a new OTP')
      ) {
        setTimeout(() => {
          nav('/login/otp');
        }, 3000);
      }
    } finally {
      setConfirmLoading(false);
    }
  }

  return (
    <div className="min-h-screen w-full flex font-lato">
      {/* Left Section - Small Branding (30%) */}
      <div className="w-[30%] bg-gradient-to-b from-[#AD0C62] to-[#621C43] text-white  flex flex-col justify-between">
        <div className="flex justify-between border-b border-[#FFF8ED] p-4">
          <h1 className="text-xl font-bold">
            MillionMakers™ <br /> Admin Portal
          </h1>
          <img src="/images/logo.png" className="h-16" alt="logo" />
        </div>
        <div className="p-6">
          <p className="text-xl leading-relaxed ">
            You can login using a phone number to receive a verification code.
          </p>
        </div>
      </div>

      {/* Right Section - OTP Form (70%) */}
      <div className="w-[70%] bg-gradient-to-b from-white to-gray-100 flex flex-col">
        <div className="">
          {/* Title */}
          <div className=" border-b border-[#A40E5F] ">
            <h2 className="p-6 text-5xl font-semibold text-[#A40E5F]  pb-6">
              Enter 6-digit login code
            </h2>
          </div>
          <div>
            <p className="p-6 text-xl text-[#661B44] ">
              We’ve sent you a 6-digit login code. <br />
              Please enter the code below to verify your digital identity.
            </p>
          </div>

          {/* OTP Input Boxes */}
          {!canResend && (
            <div className="pl-6 mb-2 text-sm text-[#A40E5F]">
              You can resend the code in {Math.floor(timer / 60)}:
              {(timer % 60).toString().padStart(2, '0')}
            </div>
          )}
          {error && <div className="pl-6 mb-4 text-red-500 text-sm">{error}</div>}

          <div className="pl-6 p-4 mt-12 flex justify-between max-w-5xl">
            {digits.map((d, i) => (
              <input
                key={i}
                ref={(el) => {
                  inputs.current[i] = el;
                }}
                value={d}
                onChange={(e) => handleChange(i, e.target.value)}
                onKeyDown={(e) => handleKeyDown(i, e)}
                inputMode="numeric"
                maxLength={1}
                disabled={confirmLoading || resendLoading}
                className="w-24 h-14 text-center text-2xl font-semibold border-b-2 text-[#A40E5F] border-[#A40E5F] focus:outline-none bg-transparent disabled:opacity-50"
              />
            ))}
          </div>

          {/* Buttons */}
          <div className=" absolute bottom-20 right-5 flex gap-4">
            <button
              onClick={() => nav('/login/otp')}
              disabled={confirmLoading || resendLoading}
              className="items-center justify-center px-4 py-3 rounded bg-gray-500 text-white hover:bg-gray-600 transition disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ArrowLeft />
            </button>
            <button
              onClick={handleResend}
              disabled={resendLoading || !canResend}
              className="w-[140px] items-center justify-center px-5 py-3 rounded bg-[#621C43] text-white hover:bg-[#501836] transition disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {/* {canResend
                ? 'Resend Code'
                : `Resend (${Math.floor(timer / 60)}:${(timer % 60).toString().padStart(2, '0')})`} */}

              {resendLoading
                ? 'Sending...'
                : canResend
                ? 'Resend Code'
                : `Resend (${Math.floor(timer / 60)}:${(timer % 60).toString().padStart(2, '0')})`}
            </button>
            <button
              onClick={handleConfirm}
              disabled={confirmLoading || digits.join('').length !== 6}
              className="w-[160px] items-center justify-center px-5 py-3 rounded bg-[#EA600E] text-white hover:bg-orange-500 transition disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {confirmLoading ? 'Verifying...' : 'Confirm Code'}
            </button>
          </div>
        </div>

        {/* Footer */}
        <div className="mt-auto flex items-center justify-end p-8">
          <p className="text-xs font-bold text-[#621C43]">Powered by Bluenumber®</p>
        </div>
      </div>
    </div>
  );
}
